<template>
    <div>
        <!-- 移动端卡片模式 -->
        <div v-if="cardMode"
            class="card bg-base-100 border border-base-200 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]">
            <div class="card-body p-4">
                <button @click="indexCheckBox = true"
                    class="flex items-center justify-between w-full text-left">
                    <div class="flex items-center space-x-3">
                        <div class="p-2 bg-info/10 rounded-lg">
                            <svg-icon name="file-index" class="w-6 h-6 fill-info" />
                        </div>
                        <div>
                            <div class="font-medium text-base-content">{{ $tt('文件索引') }}</div>
                            <div class="text-sm text-base-content/70">{{ $tt('增量或重建音乐索引') }}</div>
                        </div>
                    </div>
                    <div class="flex-shrink-0">
                        <span class="loading loading-infinity loading-md" v-if="isLoading"></span>
                        <svg v-else class="w-5 h-5 fill-current text-base-content/50" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </div>
                </button>
            </div>
        </div>

        <!-- 桌面端原有模式 -->
        <div v-else :class="['flex', 'flex-col', 'gap-2', detail ? 'w-full' : 'items-center']">
            <!-- 文件索引按钮 -->
            <button :theme="'success'" @click="indexCheckBox = true"
                :class="['btn-ghost btn btn-sm hover:bg-neutral hover:text-neutral-content', !detail ? 'tooltip tooltip-top' : '']"
                :data-tip="$tt('文件索引')" :disabled="isLoading">
                <span class="loading loading-infinity loading-md" v-if="isLoading"></span>
                <span v-else :class="['flex', 'items-center', detail ? 'w-40' : '']">
                    <svg-icon name="file-index" class="w-16 h-6 fill-current" />
                    <div v-if="detail" class="mr-auto">{{ $tt('文件索引') }}</div>
                </span>
            </button>
        </div>

        <!-- 文件索引 Drawer -->
        <div class="drawer">
            <input id="file_index" type="checkbox" class="drawer-toggle" v-model="indexCheckBox" />
            <div class="drawer-side z-50">
                <div @click="indexCheckBox = false" aria-label="close sidebar" class="drawer-overlay"></div>
                <div class="pb-20 md:pb-4 min-h-full bg-base-100 text-base-content w-full md:w-96 flex flex-col">
                    <!-- 标准头部 -->
                    <div class="flex items-center justify-between p-4 border-b border-base-200">
                        <div class="text-xl font-medium">{{ $tt('文件索引管理') }}</div>
                        <div>
                            <button class="btn btn-sm btn-neutral" @click="submitIndex" v-if="userRole === 'V2'" :disabled="isSubmitting">
                                {{ isSubmitting ? $tt('处理中...') : $tt('开始索引') }}
                            </button>
                            <button class="btn btn-sm btn-neutral opacity-50 cursor-not-allowed" v-else>{{ $tt('开始索引(V2功能)') }}</button>
                            <button class="btn btn-sm ml-1" @click="indexCheckBox = false">{{ $tt('取消') }}</button>
                        </div>
                    </div>
                    
                    <!-- 内容区域 -->
                    <div class="flex-1 p-4 space-y-6 overflow-y-auto">
                        <!-- 功能说明分组 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-info/40 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">{{ $tt('关于文件索引') }}</h3>
                                <div class="badge badge-info badge-outline badge-sm">{{ $tt('数据构建') }}</div>
                            </div>
                            <div class="ml-4 pl-3 border-l border-base-200">
                                <div class="space-y-2 text-sm text-base-content/60">
                                    <p>• <strong>{{ $tt('增量索引：') }}</strong>{{ $tt('只扫描新增或修改的文件') }}</p>
                                    <p>• <strong>{{ $tt('重建索引：') }}</strong>{{ $tt('完全重新扫描所有文件') }}</p>
                                    <p>{{ $tt('• 索引完成后可在音乐库中查看和管理文件') }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- 索引类型选择分组 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-primary/40 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">{{ $tt('索引类型') }}</h3>
                                <div class="badge badge-primary badge-outline badge-sm">{{ $tt('选择模式') }}</div>
                            </div>
                            <div class="ml-4 pl-3 border-l border-base-200 space-y-3">
                                <div class="grid grid-cols-1 gap-3">
                                    <div class="form-control">
                                        <label class="label cursor-pointer justify-start space-x-3 py-2">
                                            <input type="radio" name="indexType" value="incremental" v-model="indexForm.type"
                                                class="radio radio-primary" />
                                            <div class="flex-1">
                                                <span class="label-text font-medium">{{ $tt('增量索引') }}</span>
                                                <div class="text-xs text-base-content/60 mt-1">{{ $tt('快速扫描新增和修改的文件，适合日常维护') }}</div>
                                            </div>
                                        </label>
                                    </div>
                                    <div class="form-control">
                                        <label class="label cursor-pointer justify-start space-x-3 py-2">
                                            <input type="radio" name="indexType" value="rebuild" v-model="indexForm.type"
                                                class="radio radio-warning" />
                                            <div class="flex-1">
                                                <span class="label-text font-medium">{{ $tt('重建索引') }}</span>
                                                <div class="text-xs text-base-content/60 mt-1">{{ $tt('完整重新扫描所有文件，耗时较长但数据完整') }}</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 执行说明分组 -->
                        <div class="space-y-4">
                            <div class="flex items-center space-x-3">
                                <div class="w-1 h-5 bg-base-content/20 rounded-full"></div>
                                <h3 class="text-lg font-semibold text-base-content">{{ $tt('执行说明') }}</h3>
                                <div class="badge badge-ghost badge-sm">{{ $tt('处理流程') }}</div>
                            </div>
                            <div class="ml-4 pl-3 border-l border-base-200">
                                <div class="space-y-3">
                                    <div class="flex items-start space-x-3 p-3 bg-base-200/30 rounded-lg">
                                        <div class="w-5 h-5 bg-warning/20 rounded-full flex items-center justify-center mt-0.5">
                                            <svg class="w-3 h-3 text-warning" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                            </svg>
                                        </div>
                                        <div class="flex-1">
                                            <p class="text-sm text-base-content/70">{{ $tt('重建索引会重新处理所有文件，请根据文件数量预留充足时间') }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'
export default {
    name: 'FileIndexer',
    components: {
        SvgIcon
    },
    props: {
        detail: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        checkedData: {
            type: Array,
            default: () => []
        },
        filePath: {
            type: String,
            default: ''
        },
        userRole: {
            type: String,
            default: ''
        },
        cardMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            indexCheckBox: false,
            isSubmitting: false,
            indexForm: {
                type: 'rebuild', // incremental 或 rebuild
            }
        }
    },
    methods: {
        // 提交索引任务
        async submitIndex() {
            if (this.isSubmitting) return

            try {
                this.isSubmitting = true
                this.$emit('loading-start')

                // 准备索引参数
                const indexParams = {
                    select_data: this.checkedData,
                    file_full_path: this.filePath,
                    mode: this.indexForm.type
                }

                // 调用API创建索引任务
                this.$api.Task.fileIndex(indexParams).then(response => {
                    if (response.result) {
                        this.$myMsg.notify({
                            title: this.$tt('索引任务已启动'),
                            content: this.$tt('任务在后台努力运行中，请查看任务进度'),
                            type: 'success',
                            time: 2000,
                            url: '/#/log'
                        })
                        this.indexCheckBox = false;

                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('启动索引任务失败'),
                            content: response.message || this.$tt('未知错误'),
                            type: 'error',
                            time: 5000
                        })
                    }
                })

            } catch (error) {
                console.error('创建索引任务失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('启动索引任务失败'),
                    content: error.message || this.$tt('网络错误'),
                    type: 'error',
                    time: 5000
                })
            } finally {
                this.isSubmitting = false
                this.$emit('loading-end')
            }
        }
    }
}
</script>

<style scoped>
/* 移除不需要的样式 */
.collapse-content {
    padding-top: 1rem;
}
</style>
